/**
 * Follow-Up Engine — Admin Configuration Page
 *
 * Manages disposition rules, message templates, and follow-up sequences.
 */

import { useState } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import { useFollowUpEngine, type DispositionRule, type MessageTemplate, type SequenceStep } from "@/data/FollowUpEngineContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { EXECUTION_METHOD_LABELS, PRIORITY_CONFIG, type ExecutionMethod, type FollowUpPriority } from "@/data/unifiedFollowUp";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Settings, Plus, Pencil, Trash2, Phone, MessageCircle, PhoneCall,
  Zap, Clock, ArrowRight, ChevronDown, ChevronUp, Sparkles, GripVertical,
  ToggleLeft, AlertTriangle, Copy, Eye,
} from "lucide-react";

const METHOD_ICONS: Record<ExecutionMethod, React.ElementType> = {
  call: Phone,
  text: MessageCircle,
  call_and_text: PhoneCall,
};

const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  thank_you: "Thank You (Auto)",
  follow_up_1: "Follow-Up #1",
  follow_up_2: "Follow-Up #2",
  follow_up_3: "Follow-Up #3",
  final: "Final Touch",
};

export default function FollowUpEngine() {
  const engine = useFollowUpEngine();
  const { config } = engine;
  const { profile } = useCurrentUser();
  const repName = profile?.name || "";
  const [activeTab, setActiveTab] = useState("rules");
  const [editingRule, setEditingRule] = useState<DispositionRule | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [addingRule, setAddingRule] = useState(false);
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [addingStep, setAddingStep] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<string | null>(null);

  // ── New rule form state ──
  const [newRule, setNewRule] = useState<Omit<DispositionRule, "id">>({
    dispositionName: "", defaultFollowUpDays: 2, executionMethod: "call",
    priorityLevel: "medium", active: true, managerNotify: false, autoSendThankYou: true,
  });

  // ── New template form state ──
  const [newTemplate, setNewTemplate] = useState<Omit<MessageTemplate, "id">>({
    dispositionRuleId: "", type: "follow_up_1", label: "", body: "", autoSend: false,
  });

  // ── New sequence step state ──
  const [newStep, setNewStep] = useState<Omit<SequenceStep, "id">>({
    dispositionRuleId: "", order: 1, dayOffset: 0, actionType: "text", templateId: "", autoSend: false, label: "",
  });

  const handleSaveRule = () => {
    if (!newRule.dispositionName.trim()) { toast.error("Disposition name required"); return; }
    engine.addRule(newRule);
    setAddingRule(false);
    setNewRule({ dispositionName: "", defaultFollowUpDays: 2, executionMethod: "call", priorityLevel: "medium", active: true, managerNotify: false, autoSendThankYou: true });
    toast.success("Disposition rule added");
  };

  const handleUpdateRule = () => {
    if (!editingRule) return;
    engine.updateRule(editingRule.id, editingRule);
    setEditingRule(null);
    toast.success("Rule updated");
  };

  const handleSaveTemplate = () => {
    if (!newTemplate.body.trim() || !newTemplate.dispositionRuleId) { toast.error("Template body and rule required"); return; }
    engine.addTemplate(newTemplate);
    setAddingTemplate(false);
    setNewTemplate({ dispositionRuleId: "", type: "follow_up_1", label: "", body: "", autoSend: false });
    toast.success("Template added");
  };

  const handleSaveStep = () => {
    if (!newStep.dispositionRuleId || !newStep.label.trim()) { toast.error("Label and rule required"); return; }
    const existing = engine.getSequenceForRule(newStep.dispositionRuleId);
    engine.addSequenceStep({ ...newStep, order: existing.length + 1 });
    setAddingStep(false);
    setNewStep({ dispositionRuleId: "", order: 1, dayOffset: 0, actionType: "text", templateId: "", autoSend: false, label: "" });
    toast.success("Sequence step added");
  };

  const resolvePreview = (body: string) => {
    return engine.resolveTemplate(body, {
      customer_name: "John Smith",
      rep_name: repName || "Rep",
      project_type: "Bathroom Remodel",
    });
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
            <Zap className="h-6 w-6 text-primary" />
            Follow-Up Engine
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure disposition rules, message templates, and follow-up sequences
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">AI Suggestions</span>
            <Switch checked={config.globalSettings.aiSuggestionsEnabled} onCheckedChange={(v) => engine.updateGlobalSettings({ aiSuggestionsEnabled: v })} />
          </div>
        </div>
      </div>

      {/* Global Settings */}
      <Card className="border-primary/20">
        <CardContent className="py-4 px-5">
          <div className="flex items-center gap-8 flex-wrap">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Label className="text-xs font-semibold">Thank-You Delay</Label>
              <Input type="number" className="w-20 h-8 text-xs" value={config.globalSettings.thankYouDelayMinutes}
                onChange={(e) => engine.updateGlobalSettings({ thankYouDelayMinutes: Number(e.target.value) })} />
              <span className="text-[10px] text-muted-foreground">min</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <Label className="text-xs font-semibold">Escalation</Label>
              <Switch checked={config.globalSettings.escalationEnabled}
                onCheckedChange={(v) => engine.updateGlobalSettings({ escalationEnabled: v })} />
            </div>
            <Badge variant="outline" className="text-[10px]">{config.rules.filter(r => r.active).length} Active Rules</Badge>
            <Badge variant="outline" className="text-[10px]">{config.templates.length} Templates</Badge>
            <Badge variant="outline" className="text-[10px]">{config.sequences.length} Sequence Steps</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50">
          <TabsTrigger value="rules" className="text-xs">Disposition Rules</TabsTrigger>
          <TabsTrigger value="templates" className="text-xs">Message Templates</TabsTrigger>
          <TabsTrigger value="sequences" className="text-xs">Sequence Builder</TabsTrigger>
        </TabsList>

        {/* ═══════ RULES TAB ═══════ */}
        <TabsContent value="rules" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Define timing, method, and priority for each disposition reason.</p>
            <PermissionGate permission="leads_edit">
              <Button size="sm" className="gap-1.5 text-xs" onClick={() => setAddingRule(true)}>
                <Plus className="h-3.5 w-3.5" /> Add Rule
              </Button>
            </PermissionGate>
          </div>

          <div className="grid gap-3">
            {config.rules.map((rule) => {
              const MethodIcon = METHOD_ICONS[rule.executionMethod];
              const prCfg = PRIORITY_CONFIG[rule.priorityLevel];
              return (
                <Card key={rule.id} className={`border shadow-none transition-opacity ${!rule.active ? "opacity-50" : ""}`}>
                  <CardContent className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Switch checked={rule.active} onCheckedChange={() => engine.toggleRule(rule.id)} />
                        <div>
                          <p className="text-sm font-bold text-foreground">{rule.dispositionName}</p>
                          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{rule.defaultFollowUpDays} day{rule.defaultFollowUpDays !== 1 ? "s" : ""}</span>
                            <span className="flex items-center gap-1"><MethodIcon className="h-3 w-3" />{EXECUTION_METHOD_LABELS[rule.executionMethod]}</span>
                            <span className={`font-semibold ${prCfg.color}`}>{prCfg.label}</span>
                            {rule.managerNotify && <Badge variant="secondary" className="text-[9px] h-4">Manager Notify</Badge>}
                            {rule.autoSendThankYou && <Badge variant="outline" className="text-[9px] h-4">Auto Thank-You</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <PermissionGate permission="leads_edit">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingRule({ ...rule })}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission="leads_edit">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => { engine.deleteRule(rule.id); toast.success("Rule deleted"); }}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ═══════ TEMPLATES TAB ═══════ */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Edit message templates per disposition. Use <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{"{{customer_name}}"}</code>, <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{"{{rep_name}}"}</code>, <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{"{{project_type}}"}</code>
            </p>
            <PermissionGate permission="leads_edit">
              <Button size="sm" className="gap-1.5 text-xs" onClick={() => setAddingTemplate(true)}>
                <Plus className="h-3.5 w-3.5" /> Add Template
              </Button>
            </PermissionGate>
          </div>

          {config.rules.every(r => engine.getTemplatesForRule(r.id).length === 0) && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No templates yet. Add a template to get started.
            </div>
          )}
          {config.rules.map((rule) => {
            const tpls = engine.getTemplatesForRule(rule.id);
            if (tpls.length === 0) return null;
            return (
              <div key={rule.id}>
                <p className="text-xs font-bold text-foreground mb-2 flex items-center gap-2">
                  {rule.dispositionName}
                  {!rule.active && <span className="text-[10px] font-normal text-muted-foreground">(inactive)</span>}
                </p>
                <div className="grid gap-2 pl-3 border-l-2 border-primary/20">
                  {tpls.map((tpl) => (
                    <Card key={tpl.id} className="border shadow-none">
                      <CardContent className="px-4 py-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[9px] h-4">{TEMPLATE_TYPE_LABELS[tpl.type] || tpl.type}</Badge>
                            <span className="text-xs font-semibold">{tpl.label}</span>
                            {tpl.autoSend && <Badge className="text-[9px] h-4 bg-success/10 text-success border-success/20">Auto-Send</Badge>}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setPreviewTemplate(tpl.body)}>
                              <Eye className="h-3 w-3" />
                            </Button>
                            <PermissionGate permission="leads_edit">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditingTemplate({ ...tpl })}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </PermissionGate>
                            <PermissionGate permission="leads_edit">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                onClick={() => { engine.deleteTemplate(tpl.id); toast.success("Template deleted"); }}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </PermissionGate>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground line-clamp-2">{tpl.body}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </TabsContent>

        {/* ═══════ SEQUENCES TAB ═══════ */}
        <TabsContent value="sequences" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Configure multi-touch follow-up sequences per disposition.</p>
            <PermissionGate permission="leads_edit">
              <Button size="sm" className="gap-1.5 text-xs" onClick={() => setAddingStep(true)}>
                <Plus className="h-3.5 w-3.5" /> Add Step
              </Button>
            </PermissionGate>
          </div>

          {config.rules.every(r => engine.getSequenceForRule(r.id).length === 0) && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No sequence steps yet. Add a step to get started.
            </div>
          )}
          {config.rules.map((rule) => {
            const steps = engine.getSequenceForRule(rule.id);
            if (steps.length === 0) return null;
            return (
              <div key={rule.id}>
                <p className="text-xs font-bold text-foreground mb-2 flex items-center gap-2">
                  {rule.dispositionName}
                  {!rule.active && <span className="text-[10px] font-normal text-muted-foreground">(inactive)</span>}
                </p>
                <div className="space-y-1.5 pl-3 border-l-2 border-primary/20">
                  {steps.map((step, idx) => {
                    const StepIcon = METHOD_ICONS[step.actionType];
                    const linkedTemplate = config.templates.find(t => t.id === step.templateId);
                    return (
                      <div key={step.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-card text-[11px]">
                        <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-foreground">{step.label}</span>
                            <span className="text-muted-foreground">Day {step.dayOffset}</span>
                            <span className="flex items-center gap-0.5 text-muted-foreground"><StepIcon className="h-3 w-3" />{EXECUTION_METHOD_LABELS[step.actionType]}</span>
                            {step.autoSend && <Badge className="text-[9px] h-4 bg-success/10 text-success border-success/20">Auto</Badge>}
                            {linkedTemplate && <span className="text-[9px] text-muted-foreground">→ {linkedTemplate.label}</span>}
                          </div>
                        </div>
                        <PermissionGate permission="leads_edit">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                            onClick={() => { engine.deleteSequenceStep(step.id); toast.success("Step removed"); }}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </PermissionGate>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* ═══════ ADD RULE DIALOG ═══════ */}
      <Dialog open={addingRule} onOpenChange={setAddingRule}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-base">Add Disposition Rule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Disposition Name</Label><Input value={newRule.dispositionName} onChange={(e) => setNewRule(p => ({ ...p, dispositionName: e.target.value }))} className="h-9 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Follow-Up Days</Label><Input type="number" value={newRule.defaultFollowUpDays} onChange={(e) => setNewRule(p => ({ ...p, defaultFollowUpDays: Number(e.target.value) }))} className="h-9 text-sm" /></div>
              <div>
                <Label className="text-xs">Method</Label>
                <Select value={newRule.executionMethod} onValueChange={(v) => setNewRule(p => ({ ...p, executionMethod: v as ExecutionMethod }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(EXECUTION_METHOD_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={newRule.priorityLevel} onValueChange={(v) => setNewRule(p => ({ ...p, priorityLevel: v as FollowUpPriority }))}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2"><Switch checked={newRule.managerNotify} onCheckedChange={(v) => setNewRule(p => ({ ...p, managerNotify: v }))} /><Label className="text-xs">Manager Notify</Label></div>
              <div className="flex items-center gap-2"><Switch checked={newRule.autoSendThankYou} onCheckedChange={(v) => setNewRule(p => ({ ...p, autoSendThankYou: v }))} /><Label className="text-xs">Auto Thank-You</Label></div>
            </div>
          </div>
          <DialogFooter><PermissionGate permission="leads_edit"><Button size="sm" onClick={handleSaveRule}>Save Rule</Button></PermissionGate></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════ EDIT RULE DIALOG ═══════ */}
      <Dialog open={!!editingRule} onOpenChange={(open) => !open && setEditingRule(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-base">Edit Rule</DialogTitle></DialogHeader>
          {editingRule && (
            <div className="space-y-3">
              <div><Label className="text-xs">Disposition Name</Label><Input value={editingRule.dispositionName} onChange={(e) => setEditingRule(p => p ? { ...p, dispositionName: e.target.value } : p)} className="h-9 text-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Follow-Up Days</Label><Input type="number" value={editingRule.defaultFollowUpDays} onChange={(e) => setEditingRule(p => p ? { ...p, defaultFollowUpDays: Number(e.target.value) } : p)} className="h-9 text-sm" /></div>
                <div>
                  <Label className="text-xs">Method</Label>
                  <Select value={editingRule.executionMethod} onValueChange={(v) => setEditingRule(p => p ? { ...p, executionMethod: v as ExecutionMethod } : p)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(EXECUTION_METHOD_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={editingRule.priorityLevel} onValueChange={(v) => setEditingRule(p => p ? { ...p, priorityLevel: v as FollowUpPriority } : p)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2"><Switch checked={editingRule.managerNotify} onCheckedChange={(v) => setEditingRule(p => p ? { ...p, managerNotify: v } : p)} /><Label className="text-xs">Manager Notify</Label></div>
                <div className="flex items-center gap-2"><Switch checked={editingRule.autoSendThankYou} onCheckedChange={(v) => setEditingRule(p => p ? { ...p, autoSendThankYou: v } : p)} /><Label className="text-xs">Auto Thank-You</Label></div>
              </div>
            </div>
          )}
          <DialogFooter><PermissionGate permission="leads_edit"><Button size="sm" onClick={handleUpdateRule}>Update Rule</Button></PermissionGate></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════ ADD TEMPLATE DIALOG ═══════ */}
      <Dialog open={addingTemplate} onOpenChange={setAddingTemplate}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-base">Add Message Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Disposition Rule</Label>
              <Select value={newTemplate.dispositionRuleId} onValueChange={(v) => setNewTemplate(p => ({ ...p, dispositionRuleId: v }))}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select rule..." /></SelectTrigger>
                <SelectContent>{config.rules.map(r => <SelectItem key={r.id} value={r.id}>{r.dispositionName}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={newTemplate.type} onValueChange={(v) => setNewTemplate(p => ({ ...p, type: v as any }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TEMPLATE_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Label</Label><Input value={newTemplate.label} onChange={(e) => setNewTemplate(p => ({ ...p, label: e.target.value }))} className="h-9 text-sm" /></div>
            </div>
            <div>
              <Label className="text-xs">Message Body</Label>
              <Textarea value={newTemplate.body} onChange={(e) => setNewTemplate(p => ({ ...p, body: e.target.value }))} rows={4} className="text-sm" placeholder="Hey {{customer_name}}, this is {{rep_name}}..." />
              <p className="text-[10px] text-muted-foreground mt-1">Variables: {"{{customer_name}}"}, {"{{rep_name}}"}, {"{{project_type}}"}</p>
            </div>
            <div className="flex items-center gap-2"><Switch checked={newTemplate.autoSend} onCheckedChange={(v) => setNewTemplate(p => ({ ...p, autoSend: v }))} /><Label className="text-xs">Auto-Send</Label></div>
          </div>
          <DialogFooter><PermissionGate permission="leads_edit"><Button size="sm" onClick={handleSaveTemplate}>Save Template</Button></PermissionGate></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════ EDIT TEMPLATE DIALOG ═══════ */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-base">Edit Template</DialogTitle></DialogHeader>
          {editingTemplate && (
            <div className="space-y-3">
              <div><Label className="text-xs">Label</Label><Input value={editingTemplate.label} onChange={(e) => setEditingTemplate(p => p ? { ...p, label: e.target.value } : p)} className="h-9 text-sm" /></div>
              <div>
                <Label className="text-xs">Message Body</Label>
                <Textarea value={editingTemplate.body} onChange={(e) => setEditingTemplate(p => p ? { ...p, body: e.target.value } : p)} rows={4} className="text-sm" />
              </div>
              <div className="flex items-center gap-2"><Switch checked={editingTemplate.autoSend} onCheckedChange={(v) => setEditingTemplate(p => p ? { ...p, autoSend: v } : p)} /><Label className="text-xs">Auto-Send</Label></div>
            </div>
          )}
          <DialogFooter><PermissionGate permission="leads_edit"><Button size="sm" onClick={() => { if (editingTemplate) { engine.updateTemplate(editingTemplate.id, editingTemplate); setEditingTemplate(null); toast.success("Template updated"); } }}>Update Template</Button></PermissionGate></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════ PREVIEW DIALOG ═══════ */}
      <Dialog open={!!previewTemplate} onOpenChange={(open) => !open && setPreviewTemplate(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-base">Template Preview</DialogTitle></DialogHeader>
          {previewTemplate && (
            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <p className="text-sm leading-relaxed">{resolvePreview(previewTemplate)}</p>
              </div>
              <p className="text-[10px] text-muted-foreground">Preview uses sample data: John Smith, Mike Johnson, Bathroom Remodel</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════ ADD SEQUENCE STEP DIALOG ═══════ */}
      <Dialog open={addingStep} onOpenChange={setAddingStep}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-base">Add Sequence Step</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Disposition Rule</Label>
              <Select value={newStep.dispositionRuleId} onValueChange={(v) => setNewStep(p => ({ ...p, dispositionRuleId: v }))}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select rule..." /></SelectTrigger>
                <SelectContent>{config.rules.map(r => <SelectItem key={r.id} value={r.id}>{r.dispositionName}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Label</Label><Input value={newStep.label} onChange={(e) => setNewStep(p => ({ ...p, label: e.target.value }))} className="h-9 text-sm" /></div>
              <div><Label className="text-xs">Day Offset</Label><Input type="number" value={newStep.dayOffset} onChange={(e) => setNewStep(p => ({ ...p, dayOffset: Number(e.target.value) }))} className="h-9 text-sm" /></div>
            </div>
            <div>
              <Label className="text-xs">Action Type</Label>
              <Select value={newStep.actionType} onValueChange={(v) => setNewStep(p => ({ ...p, actionType: v as ExecutionMethod }))}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(EXECUTION_METHOD_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Linked Template (optional)</Label>
              <Select value={newStep.templateId || "none"} onValueChange={(v) => setNewStep(p => ({ ...p, templateId: v === "none" ? "" : v }))}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {config.templates.filter(t => t.dispositionRuleId === newStep.dispositionRuleId).map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2"><Switch checked={newStep.autoSend} onCheckedChange={(v) => setNewStep(p => ({ ...p, autoSend: v }))} /><Label className="text-xs">Auto-Send</Label></div>
          </div>
          <DialogFooter><PermissionGate permission="leads_edit"><Button size="sm" onClick={handleSaveStep}>Add Step</Button></PermissionGate></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
